import { Button, Input, Label, Segmented, Spinner } from '@crm/ui';
import { ArrowLeft, ArrowRight, ChevronDown, ChevronUp, Plus, Sparkles } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toErrorMessage } from '@/modules/auth/utils';
import {
  type Category,
  catalogService,
  useCategories,
  useCreateCategory,
  useCreateService,
} from '@/modules/catalog';
import { PhotoPicker } from '@/modules/onboarding/components/photo-picker';
import { useTemplates } from '@/modules/templates';
import { useAdvanceWizard } from '../api';
import { WizardFooter } from './wizard-footer';

interface RascunhoServico {
  id: string;
  name: string;
  price: string;
  durationMinutes: string;
  /** Vem do template com nome pronto, ou foi digitado na hora - so o segundo e editavel. */
  origem: 'template' | 'novo';
  /** Sim por padrao (mesma logica da tela de categorias): quem nao oferece
      desmarca, em vez de excluir a linha. */
  oferece: boolean;
}

interface RascunhoCategoria {
  id: string;
  name: string;
  photos: string[];
  indiceFoto: number;
  origem: 'template' | 'novo';
  servicos: RascunhoServico[];
}

/** Preco digitado da direita pra esquerda, sempre com duas casas decimais -
    o mesmo padrao de campo de valor em app de banco. `value`/`onChange`
    trafegam como string numerica com ponto ("80.00"), pro resto do form. */
function CampoPreco({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const centavos = Math.round((Number(value) || 0) * 100);
  const exibido = (centavos / 100).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return (
    <Input
      aria-label="Preco"
      inputMode="numeric"
      value={exibido}
      onChange={(event) => {
        const digitos = event.target.value.replace(/\D/g, '');
        onChange((Number(digitos || '0') / 100).toFixed(2));
      }}
    />
  );
}

function novoServico(): RascunhoServico {
  return {
    id: crypto.randomUUID(),
    name: '',
    price: '',
    durationMinutes: '30',
    origem: 'novo',
    oferece: true,
  };
}

function rascunhosDosTemplates(templates: {
  data: {
    id: string;
    name: string;
    photos: string[];
    services: { id: string; name: string; price: number; durationMinutes: number }[];
  }[];
}): RascunhoCategoria[] {
  return templates.data.map((categoria) => ({
    id: categoria.id,
    name: categoria.name,
    photos: categoria.photos,
    indiceFoto: 0,
    origem: 'template',
    servicos: categoria.services.map((servico) => ({
      id: servico.id,
      name: servico.name,
      price: String(servico.price),
      durationMinutes: String(servico.durationMinutes),
      origem: 'template' as const,
      oferece: true,
    })),
  }));
}

/**
 * Categorias e servicos, uma categoria por vez: primeiro a foto, depois os
 * servicos dela - so avanca pra proxima quando essa estiver pronta. Cada
 * categoria concluida ja e salva de verdade (cria categoria+servicos), entao
 * se a pessoa sair no meio e voltar depois, retoma exatamente de onde parou
 * (a posicao inicial e calculada comparando os templates com o que ja existe
 * de verdade na empresa). Ja vem preenchido com o que o SaaS admin configurou
 * no Assistente de Configuracoes (mesmos templates de
 * `categories-step.tsx`/`services-step.tsx`).
 */
export function ServicesComboStep({ onDone }: { onDone: () => void }) {
  const templates = useTemplates();
  const categoriasExistentes = useCategories({ page: 1, perPage: 100 });
  const createCategory = useCreateCategory();
  const createService = useCreateService();
  const advance = useAdvanceWizard();

  const [rascunhos, setRascunhos] = useState<RascunhoCategoria[] | null>(null);
  const [categoriaIndex, setCategoriaIndex] = useState(0);
  const [fase, setFase] = useState<'escolha' | 'foto' | 'servicos'>('escolha');
  const [comecou, setComecou] = useState(false);
  // Sim por padrao: a maioria do salao oferece mais de um tipo de servico, e
  // e mais rapido desmarcar o que nao tem do que marcar tudo do zero.
  const [escolhas, setEscolhas] = useState<Record<string, 'sim' | 'nao'>>({});
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  // Accordeon dos boxes de servico: aberto por padrao (nao entra aqui), so
  // fecha quem a pessoa recolheu na hora.
  const [colapsados, setColapsados] = useState<Set<string>>(new Set());
  const emAndamento = useRef(false);

  useEffect(() => {
    if (templates.data && rascunhos === null) {
      setRascunhos(rascunhosDosTemplates(templates.data));
    }
  }, [templates.data, rascunhos]);

  if (templates.isLoading || rascunhos === null || categoriasExistentes.isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Spinner className="h-6 w-6 text-muted-foreground" />
      </div>
    );
  }

  // Tela de boas-vindas do passo, uma vez so - antes da pergunta de quais
  // categorias entram.
  if (!comecou) {
    return (
      <div className="space-y-6 py-4 text-center">
        <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-3xl bg-primary/10">
          <Sparkles className="h-10 w-10 text-primary" />
        </div>
        <div className="space-y-2">
          <h2 className="text-lg font-semibold">
            Agora vamos cadastrar alguns serviços e suas categorias.
          </h2>
          <p className="text-sm text-muted-foreground">
            Não se preocupe se faltar algum(a), você poderá cadastrar outros na área de cadastro
            posteriormente.
          </p>
        </div>
        <Button className="w-full sm:w-auto sm:px-10" onClick={() => setComecou(true)}>
          Começar
        </Button>
      </div>
    );
  }

  // So os que a pessoa confirmou como "Sim" pedem foto e lista de servicos -
  // e neles que o resto do passo trabalha daqui pra frente.
  const categoriasAtivas = rascunhos.filter((item) => (escolhas[item.id] ?? 'sim') === 'sim');

  if (fase === 'escolha') {
    return (
      <div className="space-y-6 py-2">
        <div className="space-y-1 text-center">
          <h3 className="text-lg font-semibold">Quais tipos de serviços você oferece?</h3>
          <p className="text-sm text-muted-foreground">
            Estas respostas serão usadas para organizar o cadastro e divulgar seus serviços na
            landing page. Você poderá editá-las posteriormente.
          </p>
        </div>

        <div className="space-y-2">
          {rascunhos.map((item) => (
            <div
              key={item.id}
              className="flex flex-wrap items-center justify-between gap-3 rounded-card border border-border-strong px-4 py-3"
            >
              <Label className="font-normal">Serviços para {item.name}</Label>
              <Segmented
                label={`Serviços para ${item.name}`}
                value={escolhas[item.id] ?? 'sim'}
                onChange={(valor) => setEscolhas((atual) => ({ ...atual, [item.id]: valor }))}
                options={[
                  { value: 'sim', label: 'Sim' },
                  { value: 'nao', label: 'Não' },
                ]}
              />
            </div>
          ))}
        </div>

        <WizardFooter justify="end">
          <Button className="w-full sm:w-auto" onClick={confirmarEscolha} disabled={enviando}>
            {enviando ? <Spinner className="h-4 w-4" /> : null}
            Confirmar
            {enviando ? null : <ArrowRight className="h-4 w-4" />}
          </Button>
        </WizardFooter>
      </div>
    );
  }

  const categoria = categoriasAtivas[categoriaIndex] as RascunhoCategoria | undefined;
  if (!categoria) {
    return null;
  }

  function atualizarCategoria(campo: Partial<RascunhoCategoria>) {
    setRascunhos(
      (atual) =>
        atual?.map((item) => (item.id === categoria?.id ? { ...item, ...campo } : item)) ?? null,
    );
  }

  function atualizarServico(servicoId: string, campo: Partial<RascunhoServico>) {
    setRascunhos(
      (atual) =>
        atual?.map((item) =>
          item.id === categoria?.id
            ? {
                ...item,
                servicos: item.servicos.map((servico) =>
                  servico.id === servicoId ? { ...servico, ...campo } : servico,
                ),
              }
            : item,
        ) ?? null,
    );
  }

  /** Confirma quais categorias entram no cadastro e ja pula pra onde parou
   * (as anteriores podem ja ter sido salvas numa sessao passada). */
  function confirmarEscolha() {
    if (!rascunhos) return;
    const ativos = rascunhos.filter((item) => (escolhas[item.id] ?? 'sim') === 'sim');

    if (ativos.length === 0) {
      setEnviando(true);
      advance
        .mutateAsync({ step: 'SERVICES' })
        .then(onDone)
        .catch((error) => setErro(toErrorMessage(error)))
        .finally(() => setEnviando(false));
      return;
    }

    const nomesExistentes = new Set(
      (categoriasExistentes.data?.data ?? []).map((item) => item.name.trim().toLowerCase()),
    );
    const indice = ativos.findIndex((item) => !nomesExistentes.has(item.name.trim().toLowerCase()));
    setCategoriaIndex(indice === -1 ? Math.max(0, ativos.length - 1) : indice);
    setFase('foto');
  }

  /** Cria (se ainda nao existir) a categoria atual e os servicos dela. */
  async function salvarCategoriaAtual(): Promise<void> {
    if (!categoria) return;
    const nome = categoria.name.trim();
    if (!nome) throw new Error('De um nome pra essa categoria antes de continuar.');

    const jaExistente = (categoriasExistentes.data?.data ?? []).find(
      (item) => item.name.trim().toLowerCase() === nome.toLowerCase(),
    );

    let companyCategoryId = jaExistente?.id;
    if (!companyCategoryId) {
      const foto = categoria.photos[categoria.indiceFoto];
      const criada = (await createCategory.mutateAsync({
        name: nome,
        ...(foto ? { image: foto } : {}),
        isActive: true,
      })) as Category;
      companyCategoryId = criada.id;
    }

    const existentes = await catalogService.listServices({
      page: 1,
      perPage: 100,
      categoryId: companyCategoryId,
    });
    const servicosExistentes = new Set(
      existentes.data.map((servico) => servico.name.trim().toLowerCase()),
    );

    for (const servico of categoria.servicos) {
      if (!servico.oferece) continue;
      const nomeServico = servico.name.trim();
      if (!nomeServico || servicosExistentes.has(nomeServico.toLowerCase())) continue;

      await createService.mutateAsync({
        name: nomeServico,
        categoryId: companyCategoryId,
        price: Number(servico.price) || 0,
        durationMinutes: Number(servico.durationMinutes) || 30,
        images: [],
        isActive: true,
        maxDiscountPercent: 0,
        autoDiscountPercent: 0,
      });
      servicosExistentes.add(nomeServico.toLowerCase());
    }
  }

  async function avancar() {
    if (emAndamento.current) return;
    setErro(null);

    if (fase === 'foto') {
      if (!categoria?.name.trim()) {
        setErro('De um nome pra essa categoria antes de continuar.');
        return;
      }
      setFase('servicos');
      return;
    }

    emAndamento.current = true;
    setEnviando(true);
    try {
      await salvarCategoriaAtual();

      if (categoriaIndex >= categoriasAtivas.length - 1) {
        await advance.mutateAsync({ step: 'SERVICES' });
        onDone();
        return;
      }

      setCategoriaIndex((indice) => indice + 1);
      setFase('foto');
    } catch (error) {
      setErro(toErrorMessage(error));
    } finally {
      emAndamento.current = false;
      setEnviando(false);
    }
  }

  function voltar() {
    setErro(null);
    if (fase === 'servicos') {
      setFase('foto');
      return;
    }
    if (categoriaIndex > 0) {
      setCategoriaIndex((indice) => indice - 1);
      setFase('servicos');
      return;
    }
    // Primeira categoria ativa: "voltar" reabre a pergunta de quais
    // categorias entram, em vez de travar sem fazer nada.
    setFase('escolha');
  }

  const ehPrimeiraTela = categoriaIndex === 0 && fase === 'foto';
  const ehUltimaCategoria = categoriaIndex === categoriasAtivas.length - 1;
  const nomeEditavel = categoria.origem === 'novo';

  return (
    <div className="space-y-5 pb-20 sm:pb-0">
      {/* Trilha das categorias: bolinhas, so as ja visitadas sao clicaveis. */}
      <div className="flex flex-wrap items-center justify-center gap-2">
        {categoriasAtivas.map((item, indice) => (
          <button
            key={item.id}
            type="button"
            disabled={indice > categoriaIndex}
            onClick={() => {
              if (indice < categoriaIndex) {
                setCategoriaIndex(indice);
                setFase('servicos');
                setErro(null);
              }
            }}
            aria-label={`Categoria ${indice + 1}: ${item.name || 'sem nome'}`}
            aria-current={indice === categoriaIndex ? 'step' : undefined}
            className={
              indice === categoriaIndex
                ? 'h-2.5 w-6 rounded-full bg-primary'
                : indice < categoriaIndex
                  ? 'h-2.5 w-2.5 rounded-full bg-primary/40'
                  : 'h-2.5 w-2.5 rounded-full bg-border-strong'
            }
          />
        ))}
      </div>
      <p className="text-center text-xs text-muted-foreground">
        Categoria {categoriaIndex + 1} de {categoriasAtivas.length}
      </p>

      {erro ? <p className="text-center text-sm text-destructive">{erro}</p> : null}

      {fase === 'foto' ? (
        <div className="space-y-4 text-center">
          <div>
            <h3 className="text-base font-medium">
              {nomeEditavel
                ? 'Nome da nova categoria'
                : `Escolha uma foto para representar os serviços relacionados a ${categoria.name}`}
            </h3>
            {nomeEditavel ? null : (
              <p className="mt-1 text-sm text-muted-foreground">
                A foto que você escolher representará os serviços relacionados a "{categoria.name}"
                na Landing Page.
              </p>
            )}
          </div>

          {nomeEditavel ? (
            <Input
              aria-label="Nome da categoria"
              className="mx-auto max-w-sm text-center"
              placeholder="Ex.: Sobrancelhas"
              value={categoria.name}
              onChange={(event) => atualizarCategoria({ name: event.target.value })}
            />
          ) : null}

          <div className="flex justify-center">
            <PhotoPicker
              size="xl"
              photos={categoria.photos}
              indice={categoria.indiceFoto}
              onChange={(indiceFoto) => atualizarCategoria({ indiceFoto })}
            />
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="text-center">
            <h3 className="text-lg font-semibold">
              Agora confirme os serviços da categoria "{categoria.name}" que você oferece.
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">
              É apenas um pré-cadastro. Você poderá editá-los posteriormente.
            </p>
          </div>

          <div className="space-y-5">
            {categoria.servicos.map((servico) => {
              const fechado = colapsados.has(servico.id);

              return (
                <div key={servico.id} className="space-y-3 rounded-card border border-border p-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    {servico.origem === 'novo' ? (
                      <Input
                        aria-label="Nome do servico"
                        className="max-w-xs"
                        placeholder="Nome do serviço"
                        value={servico.name}
                        onChange={(event) =>
                          atualizarServico(servico.id, { name: event.target.value })
                        }
                      />
                    ) : (
                      <p className="font-medium">Você oferece {servico.name}?</p>
                    )}
                    <div className="flex items-center gap-1">
                      <Segmented
                        label={`Oferece ${servico.name || 'este servico'}`}
                        value={servico.oferece ? 'sim' : 'nao'}
                        onChange={(valor) => {
                          // Um serviço digitado na hora (nao veio de template) e
                          // so um rascunho vazio sem "oferece" - marcar Não so
                          // remove ele da lista, nao faz sentido deixar colapsado.
                          if (valor === 'nao' && servico.origem === 'novo') {
                            atualizarCategoria({
                              servicos: categoria.servicos.filter((item) => item.id !== servico.id),
                            });
                            return;
                          }
                          atualizarServico(servico.id, { oferece: valor === 'sim' });
                        }}
                        options={[
                          { value: 'sim', label: 'Sim' },
                          { value: 'nao', label: 'Não' },
                        ]}
                      />
                      <button
                        type="button"
                        aria-label={fechado ? 'Expandir servico' : 'Recolher servico'}
                        aria-expanded={!fechado}
                        onClick={() =>
                          setColapsados((atual) => {
                            const proximo = new Set(atual);
                            if (fechado) proximo.delete(servico.id);
                            else proximo.add(servico.id);
                            return proximo;
                          })
                        }
                        className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                      >
                        {fechado ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronUp className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>

                  {servico.oferece && !fechado ? (
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Qual a duração em minutos?</Label>
                        <Input
                          aria-label="Duracao em minutos"
                          type="number"
                          value={servico.durationMinutes}
                          onChange={(event) =>
                            atualizarServico(servico.id, { durationMinutes: event.target.value })
                          }
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Qual o preço R$?</Label>
                        <CampoPreco
                          value={servico.price}
                          onChange={(price) => atualizarServico(servico.id, { price })}
                        />
                      </div>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>

          <Button
            className="mt-6 h-11 w-full px-6 text-base sm:h-11 sm:w-auto"
            variant="outline"
            onClick={() => atualizarCategoria({ servicos: [...categoria.servicos, novoServico()] })}
          >
            <Plus className="h-5 w-5" />
            Adicionar serviço
          </Button>
        </div>
      )}

      <WizardFooter justify="between">
        <Button
          className="flex-1 sm:flex-none"
          variant="outline"
          onClick={voltar}
          disabled={ehPrimeiraTela || enviando}
        >
          <ArrowLeft className="h-4 w-4" />
          Voltar
        </Button>
        <Button className="flex-1 sm:flex-none" onClick={avancar} disabled={enviando}>
          {enviando ? <Spinner className="h-4 w-4" /> : null}
          {fase === 'servicos' && ehUltimaCategoria ? 'Concluir' : 'Avançar'}
          {enviando ? null : <ArrowRight className="h-4 w-4" />}
        </Button>
      </WizardFooter>
    </div>
  );
}
